import React, { useEffect, useMemo, useState } from "react";
import { AppBar, Toolbar, IconButton, TextField, InputAdornment, Tooltip, Box, Typography, Divider, Menu, MenuItem, ListItemIcon } from "@mui/material";
import lightThemeLogo from "../../assets/images/elynx-navy.svg";
import darkThemeLogo from "../../assets/images/elynx-white.svg";
import { useTranslation } from "react-i18next";
import { useAppSettings } from "../../contexts/app-context/AppContext";
import { useForm } from "react-hook-form";
import MenuIcon from "@mui/icons-material/Menu";
import CloudOff from "@mui/icons-material/CloudOff";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { getGroupRoute, getRouteFromPath } from "../../common/utilities/RouteHelpers";
import { asInputRef } from "../../common/utilities/AsInputRef";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { isMobile, isTablet } from 'react-device-detect';
import { Search } from "@mui/icons-material";
import MobileSearchBar from "../mobile-search-bar/MobileSearchBar";
import UserMessages from "../user-messages/UserMessages";
import { NavigationSource } from "../../common/enums/NavigationSource";
import useNavigateTo from "../../hooks/use-navigate-to/useNavigateTo";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { getActionMenuItems } from "../action-menu/BaseActions";
import { ActionMenuItem, BaseActionEnum } from "../action-menu/ActionMenuItem";
import { useLocation } from "react-router";
import { publish } from "pubsub-js";
import { EXECUTE_ACTION_TOPIC, ExecuteActionMsg } from "../../common/models/pub-sub/PubSubTopics";
import { useAppHeaderContext } from "../../contexts/app-header-context/UseAppHeaderContext";
import BackButtonV2 from "../back-button/BackButtonV2";
import AppHeaderActionMenu from "../app-header-action-menu/AppHeaderActionMenu";
import { SaveGraphImageIcon, SaveQuickGraphImageIcon } from "../action-menu/ActionMenuIcons";


export type AppHeaderV2Props = {
    hideMenu?: boolean,
    hideLogo?: boolean,
    hideSearch?: boolean,
    //hideTools?: boolean,
    onToggleDrawer?: () => void,
};

type SearchFormProp = {
    search: string
};


export default function AppHeaderV2({ onToggleDrawer,
    hideMenu,
    hideLogo,
    hideSearch,
}: AppHeaderV2Props) {

    const [subActionAnchorElements, setSubActionAnchorElements] = useState<HTMLButtonElement[]>([]);
    const { t } = useTranslation();
    const [appSettings] = useAppSettings();
    const [userState] = useUserContext();
    const {
        getValues,
        handleSubmit,
        register,
        resetField
    } = useForm();

    const [offlineState] = useOfflineDetectContext();
    const navigateTo = useNavigateTo();
    const routeContext = useRouteContext();
    const location = useLocation();
    

    // State
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const isPhone = isMobile && !isTablet;

    const {
        appHeaderContextState, 
        //setRefreshActionState,
        //setExportActionState
    } = useAppHeaderContext();


    const appHeaderTheme = useMemo(
        () => {
            return createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV2);
        },
        [appSettings.themeSet]
    );

    const mainTheme = useMemo(
        () => {
            return createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
        },
        [appSettings.themeSet]
    );

    const currentRoute = useMemo(() => {
        return getRouteFromPath(location.pathname);
    }, [
        location.pathname
    ]);


    const refreshAction = useMemo(() => {
        const item = getActionMenuItems([BaseActionEnum.Refresh]).at(0);

        if (item && (currentRoute?.viewID || routeContext.contextObject)) {
            // reset disabled state
            item.disabled = !appHeaderContextState.refreshActionEnabled;
        }

        return item;
    },[
        currentRoute?.viewID,
        routeContext.contextObject,
        appHeaderContextState
    ]);

    const refreshActionDisabled = !refreshAction || refreshAction?.disabled;

    const exportAction = useMemo(() => {

        const additionalExportActionItems: ActionMenuItem[] = [{
                label: t("actions.saveGraphImage"),
                icon: SaveGraphImageIcon,
                action: BaseActionEnum.SaveGraphImage,
                //disabled: true,
                //order: 10
            }, {
                id: "saveQuickGraph",
                label: t("actions.saveQuickGraph"),
                icon: SaveQuickGraphImageIcon,
                action: BaseActionEnum.SaveQuickGraph,
            }
        ];

        const item = getActionMenuItems([BaseActionEnum.Export]).at(0);

        if (item && (currentRoute?.viewID || routeContext.contextObject)) {
            // reset disabled state
            item.disabled = !appHeaderContextState.exportExcelEnabled &&
                !appHeaderContextState.exportPDFEnabled &&
                !appHeaderContextState.saveGraphImageEnabled &&
                !appHeaderContextState.saveQuickGraphEnabled;

            if (appHeaderContextState.saveGraphImageEnabled && item.subActions) {
                item.subActions.push(additionalExportActionItems.at(0)!);
            }

            if (appHeaderContextState.saveQuickGraphEnabled && item.subActions) {
                item.subActions.push(additionalExportActionItems.at(1)!);
            }
        }

        return item;
    },[
        currentRoute?.viewID,
        routeContext.contextObject,
        appHeaderContextState,
        t
    ]);

    const exportActionDisabled = !exportAction || 
                                 exportAction?.disabled || 
                                (!appHeaderContextState.exportExcelEnabled &&
                                 !appHeaderContextState.exportPDFEnabled &&
                                 !appHeaderContextState.saveGraphImageEnabled &&
                                 !appHeaderContextState.saveQuickGraphEnabled);    
    
    const handleSearch = (data: unknown) => {

        //alert(`Search for ${JSON.stringify(data)}`);
        const searchData = data as SearchFormProp;

        if (searchData.search) {

            const text = searchData.search.trim();
            if (text.length > 0) {

                const url = `${getGroupRoute(UIViewEnum.SearchResults, routeContext.groupID as number)}&token=${encodeURIComponent(text)}`;
                navigateTo(url);
            }
        }
    };

    // const onMaximizeView = () => {
    //     dispatchAppSettings({
    //         type: AppSettingsActionTypeEnum.MaximizeContent,
    //         on: !appSettings.maximizeContent
    //     });
    // };

    function onRefreshAction() {
        publish(EXECUTE_ACTION_TOPIC, {
            action: BaseActionEnum.Refresh
        } as ExecuteActionMsg);
    }

    function onExportAction(item: ActionMenuItem) {
        publish(EXECUTE_ACTION_TOPIC, {
            action: item.action
        } as ExecuteActionMsg);
    }

    useEffect(() => {

        if (routeContext.navSrc === NavigationSource.SearchResults) {

            const searchValue: string | undefined = getValues("search");

            if ((searchValue?.length ?? 0) > 0) {

                resetField("search");
            }
        }
    }, [
        getValues,
        resetField,
        routeContext.navSrc
    ]);

    return (
        <>
            {/* Standard AppBar */}
            {!mobileSearchOpen &&
                <ThemeProvider theme={appHeaderTheme}>
                    <AppBar position="absolute" sx={{
                        zIndex: theme => isPhone ? theme.zIndex.appBar : theme.zIndex.drawer + 1,
                        transition: theme => theme.transitions.create(["width", "margin"], {
                            easing: theme.transitions.easing.sharp,
                            duration: theme.transitions.duration.leavingScreen,
                        })

                    }}>
                        <Toolbar
                            //variant={appSettings.maximizeContent ? 'dense' : undefined}
                            sx={{
                                paddingLeft: theme => theme.spacing(2),
                                paddingRight: theme => theme.spacing(2),

                            }} >
                            {/* Toggle Drawer Button */}

                            {!hideMenu &&
                                <IconButton
                                    edge="start"
                                    color="inherit"
                                    aria-label="open drawer"
                                    onClick={() => {
                                        if (onToggleDrawer) {
                                            onToggleDrawer();
                                        }
                                    }}>
                                    <Tooltip title={t("appHeader.toggleMenu") as string}>
                                        <MenuIcon />
                                    </Tooltip>
                                </IconButton>
                            }


                            {!hideLogo &&
                                <img
                                    src={
                                        appHeaderTheme.palette.mode === "light"
                                            ? lightThemeLogo
                                            : darkThemeLogo
                                    }
                                    alt="eLynx Technologies"
                                    style={{
                                        height: 32
                                    }}
                                />
                            }
                            <Box sx={{
                                display: "flex",
                                marginLeft: "auto",
                                alignItems: 'center'
                            }}>
                                <Typography
                                    variant="body2"
                                    color='transparent'
                                    sx={{
                                        marginLeft: 1,
                                        marginRight: 1,
                                        color: 'transparent'
                                    }}>{userState.customer?.id}</Typography>
                                {

                                    offlineState.offline && !hideSearch &&
                                    <Box sx={{
                                        display: 'flex',
                                        marginTop: 'auto',
                                        marginBottom: 'auto'
                                    }}>
                                        <CloudOff
                                            sx={{
                                                marginRight: theme => theme.spacing(1)
                                            }}
                                            fontSize='small' />
                                        {t('appHeader.offline')}
                                    </Box>
                                }

                                <BackButtonV2 color="inherit" />

                                {!hideSearch && !offlineState.offline &&
                                    <>
                                        <Box
                                            sx={{
                                                //display: appSettings.maximizeContent ? 'none' : { xs: "none", sm: "block" } 
                                                display: { xs: "none", sm: "block" }
                                            }}
                                        >
                                            {/* Make TextField think it's being used with a dark theme */}
                                            {/* <ThemeProvider theme={theme}> */}
                                            {/* Desktop Search Input */}
                                            <form onSubmit={handleSubmit(handleSearch)}>
                                                <TextField
                                                    variant="filled"
                                                    label={t("appHeader.search")}
                                                    color="secondary"
                                                    size="small"
                                                    {...asInputRef(register("search"))}
                                                    slotProps={{
                                                        input: {
                                                            endAdornment:
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        color="inherit"
                                                                        type="submit"
                                                                        aria-label="Search entire site">
                                                                        <Search />
                                                                    </IconButton>
                                                                </InputAdornment>
                                                        }
                                                    }} />
                                            </form>
                                            {/* </ThemeProvider> */}
                                        </Box>

                                        {/* Mobile Search Button */}
                                        {isPhone && <Box sx={{ display: appSettings.maximizeContent ? 'block' : { xs: "block", sm: "none" } }}>
                                            <IconButton color="inherit"
                                                aria-label="Search entire site"
                                                onClick={() => setMobileSearchOpen(true)}>
                                                <Search />
                                            </IconButton>
                                        </Box>}
                                    </>}

                                {/* Desktop Tools */}
                                {!isPhone && <Box
                                    sx={{
                                        //display: "block" 
                                        display: 'inline-flex'
                                    }}
                                >

                                    {
                                        !offlineState.offline &&
                                        <>

                                            {/* <Divider orientation='vertical'
                                        flexItem={true} 
                                        style={{borderRightWidth: "medium!important"}}
                                        sx={{
                                            marginLeft: 1,
                                            marginTop: '6px',
                                            borderColor: "inherit",
                                            borderRightWidth: isPhone ? 'medium' : undefined
                                        }} 
                                     /> */}

                                            {!isPhone && <IconButton size="small"
                                                disabled={refreshActionDisabled}
                                                aria-label="refresh"
                                                color='inherit'

                                                sx={{
                                                    color: "inherit",
                                                    // color: refreshActionDisabled ? 'white!important' : undefined,
                                                    // opacity: refreshActionDisabled ? .3 : undefined
                                                }}
                                                onClick={() => {
                                                    onRefreshAction();
                                                }}
                                            >
                                                <Tooltip title='Refresh'>
                                                    {refreshAction?.icon ?
                                                        <refreshAction.icon />
                                                        : <></>}
                                                </Tooltip>
                                            </IconButton>}



                                            <IconButton
                                                id={exportAction?.id}
                                                size="small"
                                                disabled={exportActionDisabled}
                                                aria-label='Export'
                                                //edge="end"
                                                color='inherit'
                                                onClick={(event) => {
                                                    setSubActionAnchorElements([...subActionAnchorElements, event.currentTarget]);
                                                }}
                                                sx={{
                                                    color: "inherit",
                                                    // color: exportActionDisabled ? 'white!important' : undefined,
                                                    // opacity: exportActionDisabled ? .3 : undefined,
                                                    //paddingTop: 2
                                                }}
                                            >
                                                {/* <Box sx={{
                                            display: "flex",
                                            flex: "0 0 auto",
                                            border: theme => o.selected
                                                ? `solid 1px ${theme.palette.text.primary}`
                                                : "none",
                                            borderRadius: "4px"
                                        }}> */}
                                                <Tooltip title='Export'>
                                                    {exportAction?.icon ?
                                                        <exportAction.icon />
                                                        : <></>}
                                                </Tooltip>
                                                {/* </Box> */}
                                            </IconButton>
                                            {exportAction?.subActions &&

                                                <ThemeProvider theme={mainTheme}>
                                                    <Menu
                                                        id={`subaction-menu`}
                                                        color='textPrimary'
                                                        anchorEl={subActionAnchorElements.find(v => v.id === exportAction.id)}
                                                        anchorOrigin={{
                                                            vertical: 'bottom',
                                                            horizontal: 'center'
                                                        }}
                                                        transformOrigin={{
                                                            vertical: 'top',
                                                            horizontal: 'center'
                                                        }}
                                                        keepMounted
                                                        open={Boolean(subActionAnchorElements.find(v => v.id === exportAction.id))}
                                                        onClose={() => setSubActionAnchorElements(subActionAnchorElements.filter(v => v.id !== exportAction.id))}
                                                    >
                                                        {exportAction.subActions.map((item, index) => (
                                                            <MenuItem
                                                                key={`mi${index}`}
                                                                disabled={item.action === BaseActionEnum.Excel 
                                                                    ? !appHeaderContextState.exportExcelEnabled 
                                                                    : item.action === BaseActionEnum.PDF
                                                                        ? !appHeaderContextState.exportPDFEnabled
                                                                        : item.action === BaseActionEnum.SaveQuickGraph
                                                                            ? !appHeaderContextState.saveQuickGraphEnabled
                                                                            : item.action === BaseActionEnum.SaveGraphImage
                                                                                ? !appHeaderContextState.saveGraphImageEnabled
                                                                                : false
                                                                }
                                                                onClick={() => {
                                                                    setSubActionAnchorElements(subActionAnchorElements.filter(v => v.id !== exportAction.id));
                                                                    onExportAction(item);
                                                                }}>
                                                                {item.icon &&
                                                                    <ListItemIcon id={`${item.id}`} sx={{ minWidth: appHeaderTheme.spacing(4) }}>
                                                                        {item.checked && item.checkedIcon ?
                                                                            <item.checkedIcon /> :
                                                                            <item.icon />
                                                                        }
                                                                    </ListItemIcon>
                                                                }
                                                                <Typography variant='inherit'>
                                                                    {item.label}
                                                                </Typography>
                                                            </MenuItem>

                                                        ))}
                                                    </Menu>
                                                </ThemeProvider>

                                            }

                                            <Divider orientation='vertical'
                                                flexItem={true}
                                                sx={{
                                                    marginLeft: 1,
                                                    marginTop: '6px',
                                                    borderColor: "inherit",
                                                    borderRightWidth: isPhone ? 'medium' : undefined
                                                }}
                                            />
                                            <AppHeaderActionMenu />

                                            <Divider orientation='vertical'
                                                flexItem={true}
                                                style={{ borderRightWidth: "medium!important" }}
                                                sx={{
                                                    marginLeft: 1,
                                                    marginTop: '6px',
                                                    borderColor: "inherit",
                                                    borderRightWidth: isPhone ? 'medium' : undefined
                                                }}
                                            />

                                            <UserMessages />
                                        </>
                                    }


                                    {/* <IconButton color={appSettings.maximizeContent ? 'secondary' : 'inherit'}
                                    onClick={() => onMaximizeView()}>
                                    <Tooltip title={t("appHeader.tools.maximizeview") as string}>
                                        <SettingsOverscan />
                                    </Tooltip>
                                </IconButton> */}
                                </Box>}
                            </Box>
                        </Toolbar>
                    </AppBar>
                </ThemeProvider>

            }
            {/* Mobile Search AppBar */}
            {mobileSearchOpen &&
                <MobileSearchBar onClose={() => setMobileSearchOpen(false)} />
            }
        </>
    );
}
